import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useId, useState } from 'react'
import { ApiError } from '@/api/client'
import { topicsApi } from '@/api/topics'
import { workspacesApi } from '@/api/workspaces'
import { MemberList } from '@/components/MemberList'
import { RefreshFailed } from '@/components/RefreshFailed'
import { adminCopy, settingsCopy } from '@/copy'
import { useToastStore } from '@/store/toast'
import type {
  RouterConfig,
  TopicInfo,
  UserInfo,
  WorkspaceInfo,
} from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { DisclosureChevron } from '@/ui/Disclosure/Disclosure'
import { Field, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import {
  isOwnRoleRefusal,
  MEMBER_ROLES,
  membersKey,
  useWorkspaceMembersQuery,
} from '../../hooks/useWorkspaceMembers'
import { RouterConfigPanel } from './RouterConfigPanel'
import styles from './WorkspaceRow.module.css'

interface WorkspaceRowProps {
  workspace: WorkspaceInfo
  allUsers: UserInfo[]
  expanded: boolean
  currentUserId: string | null
  canManageMembers: boolean
  canTransferOwnership: boolean
  canDelete: boolean
  deletionPending: boolean
  isDeleting: boolean
  onToggle: () => void
  onRequestDelete: () => void
  onRetryDelete: () => void
}

export function WorkspaceRow({
  workspace,
  allUsers,
  expanded,
  currentUserId,
  canManageMembers,
  canTransferOwnership,
  canDelete,
  deletionPending,
  isDeleting,
  onToggle,
  onRequestDelete,
  onRetryDelete,
}: WorkspaceRowProps) {
  const panelId = useId()
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const [configuringRouter, setConfiguringRouter] = useState<string | null>(
    null,
  )
  const [addingMember, setAddingMember] = useState(false)
  const [newMemberUsername, setNewMemberUsername] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('member')
  const userManaged = (workspace.management_kind ?? 'user') === 'user'
  const workspaceActive = (workspace.lifecycle_status ?? 'active') === 'active'
  const memberControlsEnabled = canManageMembers && workspaceActive

  const membersQuery = useWorkspaceMembersQuery(workspace.workspace_id, {
    enabled: expanded && userManaged,
  })
  const members = membersQuery.data ?? []
  const showMembersSkeleton = useSkeletonDelay(membersQuery.isPending)

  const topicsQuery = useQuery<TopicInfo[]>({
    queryKey: ['workspace', workspace.workspace_id, 'topics'],
    queryFn: () => topicsApi.listForWorkspace(workspace.workspace_id),
    enabled: expanded,
  })
  const topics = topicsQuery.data ?? []
  const showTopicsSkeleton = useSkeletonDelay(topicsQuery.isPending)

  const invalidateMembershipAuthority = () => {
    void qc.invalidateQueries({
      queryKey: membersKey(workspace.workspace_id),
    })
    void qc.invalidateQueries({ queryKey: ['admin', 'workspaces'] })
    void qc.invalidateQueries({ queryKey: ['me'] })
  }

  const addMemberMutation = useMutation({
    mutationFn: ({ username, role }: { username: string; role: string }) =>
      workspacesApi.addMember(workspace.workspace_id, username, role),
    onSuccess: () => {
      invalidateMembershipAuthority()
      setAddingMember(false)
    },
    onError: (error) =>
      addToast(
        error instanceof ApiError && error.status === 404
          ? settingsCopy.members.addNotFound
          : settingsCopy.members.addFailed,
        'error',
      ),
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      workspacesApi.removeMember(workspace.workspace_id, userId),
    onSuccess: invalidateMembershipAuthority,
    onError: (error, removedUserId) =>
      addToast(
        isOwnRoleRefusal(error, removedUserId, currentUserId)
          ? settingsCopy.members.removeOwnFailed
          : settingsCopy.members.removeFailed,
        'error',
      ),
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      workspacesApi.updateMember(workspace.workspace_id, userId, role),
    onSuccess: invalidateMembershipAuthority,
    onError: (error, variables) =>
      addToast(
        isOwnRoleRefusal(error, variables.userId, currentUserId)
          ? settingsCopy.members.roleOwnFailed
          : settingsCopy.members.roleFailed,
        'error',
      ),
  })

  const transferOwnershipMutation = useMutation({
    mutationFn: (userId: string) =>
      workspacesApi.transferOwnership(workspace.workspace_id, userId),
    onSuccess: invalidateMembershipAuthority,
    onError: () => addToast(settingsCopy.members.transferFailed, 'error'),
  })

  const setRouterMutation = useMutation({
    mutationFn: ({
      topicId,
      config,
    }: {
      topicId: string
      config: RouterConfig | null
    }) =>
      config
        ? topicsApi.setRouterConfig(topicId, config)
        : topicsApi.clearRouterConfig(topicId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['workspace', workspace.workspace_id, 'topics'],
      })
      setConfiguringRouter(null)
    },
    onError: () => addToast(adminCopy.workspaces.routerFailed, 'error'),
  })

  const memberUserIds = new Set(members.map((m) => m.user_id))
  const nonMembers = allUsers.filter((u) => !memberUserIds.has(u.user_id))

  const addMember = addingMember ? (
    <form
      className={styles.add}
      onSubmit={(e) => {
        e.preventDefault()
        if (newMemberUsername) {
          addMemberMutation.mutate({
            username: newMemberUsername,
            role: newMemberRole,
          })
        }
      }}
      onKeyDown={(e) => e.key === 'Escape' && setAddingMember(false)}
    >
      <Inline gap="space-8" align="end">
        <Field label={adminCopy.workspaces.userToAdd} className={styles.user}>
          <Select
            value={newMemberUsername}
            onChange={(e) => setNewMemberUsername(e.target.value)}
          >
            <option value="">{adminCopy.workspaces.selectUser}</option>
            {nonMembers.map((u) => (
              <option key={u.user_id} value={u.username}>
                {u.username}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={adminCopy.workspaces.role}>
          <Select
            value={newMemberRole}
            onChange={(e) => setNewMemberRole(e.target.value)}
          >
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          type="submit"
          variant="primary"
          disabled={!newMemberUsername}
          loading={addMemberMutation.isPending}
        >
          {adminCopy.workspaces.add}
        </Button>
        <Button
          variant="outline"
          aria-label={adminCopy.workspaces.cancelAdd}
          onClick={() => setAddingMember(false)}
        >
          {settingsCopy.members.cancel}
        </Button>
      </Inline>
    </form>
  ) : (
    <Button
      variant="ghost"
      onClick={() => {
        setAddingMember(true)
        setNewMemberUsername('')
        setNewMemberRole('member')
      }}
    >
      <Icon name="plus" size="icon-embedded" />
      {adminCopy.workspaces.addMember}
    </Button>
  )

  return (
    <>
      <RowListItem>
        <Avatar name={workspace.name} kind="workspace" size="md" />
        <Inline gap="space-8" align="baseline" className={styles.main}>
          <Text>{workspace.name}</Text>
          <Text variant="code" tone="text-mute">
            {workspace.slug}
          </Text>
        </Inline>
        {canDelete && !deletionPending && (
          <Button
            variant="ghost"
            disabled={!workspaceActive}
            loading={isDeleting}
            onClick={onRequestDelete}
          >
            {adminCopy.workspaces.delete}
          </Button>
        )}
        {canDelete && deletionPending && (
          <Button
            variant="outline"
            loading={isDeleting}
            onClick={onRetryDelete}
          >
            {adminCopy.workspaces.retryDelete}
          </Button>
        )}
        <IconButton
          label={adminCopy.expandWorkspace(workspace.name)}
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}
          onClick={onToggle}
        >
          <DisclosureChevron open={expanded} />
        </IconButton>
      </RowListItem>

      {expanded && (
        <RowListItem id={panelId} className={styles.panel}>
          {userManaged && (
            <section>
              <SectionHeader as="h3" rule trailingSpacing="section">
                {adminCopy.workspaces.members}
              </SectionHeader>
              <Stack gap="space-12">
                {membersQuery.isPending ? (
                  <div aria-busy="true">
                    {showMembersSkeleton && <Skeleton lines={2} />}
                  </div>
                ) : membersQuery.isError && !membersQuery.data ? (
                  <Alert tone="danger">
                    <Inline gap="space-8" align="baseline" wrap>
                      <Text>{adminCopy.loadFailed.members}</Text>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void membersQuery.refetch()}
                      >
                        {adminCopy.retry}
                      </Button>
                    </Inline>
                  </Alert>
                ) : (
                  <>
                    {membersQuery.isError && (
                      <RefreshFailed
                        message={adminCopy.refreshFailed.members}
                        onRetry={() => void membersQuery.refetch()}
                      />
                    )}
                    <MemberList
                      members={members}
                      canManage={memberControlsEnabled}
                      onUpdateRole={(userId, role) =>
                        updateRoleMutation.mutate({ userId, role })
                      }
                      onRemove={(userId) => removeMemberMutation.mutate(userId)}
                      isUpdating={updateRoleMutation.isPending}
                      isRemoving={removeMemberMutation.isPending}
                      currentUserId={currentUserId}
                      canTransferOwnership={
                        canTransferOwnership && memberControlsEnabled
                      }
                      onTransferOwnership={(userId) =>
                        transferOwnershipMutation.mutate(userId)
                      }
                      isTransferring={transferOwnershipMutation.isPending}
                      footer={memberControlsEnabled ? addMember : undefined}
                    />
                  </>
                )}
              </Stack>
            </section>
          )}

          {(topicsQuery.isPending
            ? showTopicsSkeleton
            : topics.length > 0 || topicsQuery.isError) && (
            <section aria-busy={topicsQuery.isPending || undefined}>
              <SectionHeader as="h3" rule trailingSpacing="section">
                {adminCopy.workspaces.topics}
              </SectionHeader>
              <Stack gap="space-12">
                {topicsQuery.isPending ? (
                  <Skeleton lines={2} />
                ) : topicsQuery.isError && !topicsQuery.data ? (
                  <Alert tone="danger">
                    <Inline gap="space-8" align="baseline" wrap>
                      <Text>{adminCopy.loadFailed.topics}</Text>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void topicsQuery.refetch()}
                      >
                        {adminCopy.retry}
                      </Button>
                    </Inline>
                  </Alert>
                ) : (
                  <>
                    {topicsQuery.isError && (
                      <RefreshFailed
                        message={adminCopy.refreshFailed.topics}
                        onRetry={() => void topicsQuery.refetch()}
                      />
                    )}
                    <RowList
                      variant="plain"
                      aria-label={adminCopy.workspaces.topicsLabel}
                    >
                      {topics.map((topic) => {
                        const routerConfig =
                          topic.router_config as RouterConfig | null
                        const routable = topic.agents.length > 1
                        return (
                          <Fragment key={topic.topic_id}>
                            <RowListItem>
                              <Text tone="text-sub" className={styles.main}>
                                {topic.name}
                              </Text>
                              {routable &&
                                (routerConfig ? (
                                  <Chip
                                    variant="kind"
                                    label={routerConfig.model}
                                  />
                                ) : (
                                  <Text variant="caption" tone="text-mute">
                                    {adminCopy.workspaces.noRouter}
                                  </Text>
                                ))}
                              {routable && workspaceActive && (
                                <Button
                                  variant="ghost"
                                  aria-expanded={
                                    configuringRouter === topic.topic_id
                                  }
                                  onClick={() =>
                                    setConfiguringRouter((prev) =>
                                      prev === topic.topic_id
                                        ? null
                                        : topic.topic_id,
                                    )
                                  }
                                >
                                  {adminCopy.workspaces.edit}
                                </Button>
                              )}
                            </RowListItem>
                            {workspaceActive &&
                              routable &&
                              configuringRouter === topic.topic_id && (
                                <RowListItem className={styles.panel}>
                                  <RouterConfigPanel
                                    current={routerConfig ?? undefined}
                                    isSaving={setRouterMutation.isPending}
                                    onSave={(config) =>
                                      setRouterMutation.mutate({
                                        topicId: topic.topic_id,
                                        config,
                                      })
                                    }
                                    onCancel={() => setConfiguringRouter(null)}
                                  />
                                </RowListItem>
                              )}
                          </Fragment>
                        )
                      })}
                    </RowList>
                  </>
                )}
              </Stack>
            </section>
          )}
        </RowListItem>
      )}
    </>
  )
}
